import { data } from 'react-router-dom';
import api from './axiosConfig';

class ApiService {
  // Users API
  async fetchUsers() {
    try {
      const response = await api.get('/api/admin/users');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch users',
        errorType: error.response?.data?.error || 'fetch_users_failed',
      };
    }
  }

  async fetchUserById(userId) {
    try {
      const response = await api.get(`/api/admin/users/${userId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch user',
        errorType: error.response?.data?.error || 'fetch_user_failed',
      };
    }
  }

  // AdminUsers
  async fetchAdminUsers() {
    try {
      const response = await api.get('/api/admin/users'); // endpoint
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching admin users:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch admin users',
        errorType: error.response?.data?.error || 'fetch_admin_users_failed',
      };
    }
  }

  // ADD Roles
  async addRolesToUsers(adminUserId, roles = []) {
    try {
      const response = await api.post(
        `/api/admin/admin-users/${adminUserId}/add-roles`,
        { roles },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error adding roles to user:', error);
      return {
        success: false,
        error: error.response?.data?.manage || 'Failed to add roles to user',
        errorType: error.response?.data?.error || 'add_roles_failed',
      };
    }
  }

  // Remove Role
  async removeRolesFromUser(userId) {
    try {
      const response = await api.delete(
        `/api/admin/admin-users/${userId}/remove-roles`,
        { data: {} },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting admin user:', error);
      return {
        success: false,
        error: error.response?.data?.manage || 'Failed to remove roles',
        errorType: error.response?.data?.error || 'remove_roles_failed',
      };
    }
  }
  //

  // Fetch Prediction Models
  async fetchModels(isActive = null, isDeleted = null) {
    try {
      const params = {};

      if (isActive !== null) params.is_active = isActive;
      if (isDeleted !== null) params.is_deleted = isDeleted;

      const response = await api.get('/api/admin/models', { params });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch models',
        errorType: error.response?.data?.error || null,
      };
    }
  }
  //

  // Fetch Model By ID
  async fetchModelById(modelId) {
    try {
      const response = await api.get(`/api/admin/models/${modelId}`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch model',
        errorType: error.response?.data?.status,
      };
    }
  }

  // Fetch training req by ID
  async fetchTrainingRequestByModelId(modelId) {
    try {
      const response = await api.get(
        `/api/admin/train-model-requests/by-model/${modelId}`,
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to fetch training request',
        errorType: error.response?.data?.status,
      };
    }
  }

  //

  // Fetch Model Details
  async fetchModelDetails(model_id, period = 'day') {
    try {
      const response = await api.get(`/api/admin/models/${model_id}/global`, {
        params: { period },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Erorr fetching model details:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch model details',
      };
    }
  }

  // Prediction Count Chart
  async fetchPredictionTrends(model_id, period = 'day') {
    try {
      const response = await api.get(`/api/admin/models/${model_id}/global`, {
        params: { period },
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching prediction trends:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to fetch prediction trends',
      };
    }
  }

  async fetchProbabilityDistribution(model_id, group_by = 'prediction') {
    try {
      const response = await api.get(
        `/api/admin/models/${model_id}/probability-distribution`,
        { params: { group_by: group_by } },
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching distribution:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch distribution',
      };
    }
  }

  async fetchPredictionMetadata(model_id) {
    try {
      const response = await api.get(`/api/models/prediction-metadata`, {
        params: { model_id },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch prediction metadata',
      };
    }
  }

  async fetchModelLatency(model_id) {
    try {
      const response = await api.get(
        `/api/models/prediction-latency/${model_id}`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch model latency',
      };
    }
  }

  async fetchTrainModelRequests() {
    try {
      const response = await api.get(`/api/admin/train-model-requests`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching train model requests:', error);
      return { success: false };
    }
  }

  // A/B Testing
  async updateABTestConfig(modelId, payload) {
    try {
      const res = await api.patch(
        `/api/admin/models/${modelId}/ab-config`,
        payload,
      );
      return { success: true, data: res.data };
    } catch (err) {
      console.error('AB config update failed:', err);
      return {
        success: false,
        error: err.response?.data?.detail || 'Failed to update A/B config',
      };
    }
  }

  async fetchABTestStats(modelId) {
    try {
      const res = await api.get(
        `/api/admin/prediction-models/${modelId}/ab-stats`,
      );
      return { success: true, data: res.data };
    } catch (err) {
      console.error('AB stats fetch failed:', err);
      return {
        success: false,
        error: err.response?.data?.detail || 'Failed to fetch A/B stats',
      };
    }
  }
  //

  async fetchUserCompaniesAndOrganizations(userId) {
    try {
      const response = await api.get(
        `/api/admin/users/${userId}/companies-and-organizations`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching user companies and organizations:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch user companies and organizations',
        errorType:
          error.response?.data?.error ||
          'fetch_user_companies_organizations_failed',
      };
    }
  }

  async updateUserActivation(userId, activated) {
    try {
      const response = await api.put(
        `/api/user/admin/${userId}/activate?activated=${activated}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating user activation:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to update user activation status',
        errorType:
          error.response?.data?.error || 'update_user_activation_failed',
      };
    }
  }

  async sendActivationEmail(userId) {
    try {
      const response = await api.post(
        `/api/admin/users/${userId}/send-activation-email`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error sending activation email:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to send activation email',
        errorType:
          error.response?.data?.error || 'send_activation_email_failed',
      };
    }
  }

  async sendResetPasswordEmail(userId) {
    try {
      const response = await api.post(
        `/api/admin/users/${userId}/send-reset-password-email`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error sending reset password email:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to send reset password email',
        errorType:
          error.response?.data?.error || 'send_reset_password_email_failed',
      };
    }
  }

  async fetchUserEmailsSent(userId) {
    try {
      const response = await api.get(`/api/admin/users/${userId}/emails-sent`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching user emails sent:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to fetch user emails sent',
        errorType:
          error.response?.data?.error || 'fetch_user_emails_sent_failed',
      };
    }
  }

  async deleteUser(userId) {
    try {
      const response = await api.delete(`/api/admin/users/${userId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting user:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete user',
        errorType: error.response?.data?.error || 'delete_user_failed',
      };
    }
  }

  // User Cron Jobs API
  async fetchUserAutoDetectReporter(userId) {
    try {
      const response = await api.get(
        `/api/admin/users/${userId}/auto-detect-reporter`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error(
        'Error fetching user auto-detect reporter settings:',
        error,
      );
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch auto-detect reporter settings',
        errorType:
          error.response?.data?.error || 'fetch_auto_detect_reporter_failed',
      };
    }
  }

  async updateUserAutoDetectReporterEnabled(userId, enabled) {
    try {
      const response = await api.put(
        `/api/admin/users/${userId}/auto-detect-reporter/enabled`,
        {
          enabled: enabled,
        },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error(
        'Error updating user auto-detect reporter enabled status:',
        error,
      );
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to update auto-detect reporter status',
        errorType:
          error.response?.data?.error || 'update_auto_detect_reporter_failed',
      };
    }
  }

  async fetchUsersPaginated(params = {}) {
    try {
      const {
        page = 1,
        limit = 50,
        include_sensitive = false,
        search = null,
        sort_by = 'create_date',
        sort_order = 'desc',
      } = params;

      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());
      queryParams.append('include_sensitive', include_sensitive.toString());

      if (search && search.trim()) {
        queryParams.append('search', search.trim());
      }

      queryParams.append('sort_by', sort_by);
      queryParams.append('sort_order', sort_order);

      const response = await api.get(
        `/api/admin/users/paginated?${queryParams.toString()}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching paginated users:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch users',
        errorType: error.response?.data?.error || 'fetch_users_failed',
      };
    }
  }

  // Companies API
  async fetchCompanies(queryParams = {}) {
    try {
      const params = new URLSearchParams();

      // Handle is_test parameter
      if (queryParams.is_test !== null && queryParams.is_test !== undefined) {
        params.append('is_test', queryParams.is_test.toString());
      }

      // Add any other query parameters
      Object.keys(queryParams).forEach((key) => {
        if (
          key !== 'is_test' &&
          queryParams[key] !== null &&
          queryParams[key] !== undefined
        ) {
          params.append(key, queryParams[key].toString());
        }
      });

      const url = `/api/admin/companies${
        params.toString() ? `?${params.toString()}` : ''
      }`;
      const response = await api.get(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching companies:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch companies',
        errorType: error.response?.data?.error || 'fetch_companies_failed',
      };
    }
  }

  async fetchCompaniesPaginated(params = {}) {
    try {
      const {
        page = 1,
        limit = 50,
        search = null,
        sort_by = 'create_date',
        sort_order = 'desc',
        is_test = null,
      } = params;

      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());

      if (search && search.trim()) {
        queryParams.append('search', search.trim());
      }

      queryParams.append('sort_by', sort_by);
      queryParams.append('sort_order', sort_order);

      if (is_test !== null && is_test !== undefined) {
        queryParams.append('is_test', is_test.toString());
      }

      const response = await api.get(
        `/api/admin/companies/paginated?${queryParams.toString()}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching paginated companies:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch companies',
        errorType: error.response?.data?.error || 'fetch_companies_failed',
      };
    }
  }

  async fetchCompanyById(companyId) {
    try {
      const response = await api.get(`/api/admin/companies/${companyId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching company by ID:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch company',
        errorType: error.response?.data?.error || 'fetch_company_failed',
      };
    }
  }

  async deleteCompany(companyId) {
    try {
      const response = await api.delete(`/api/admin/companies/${companyId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting company:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete company',
        errorType: error.response?.data?.error || 'delete_company_failed',
      };
    }
  }

  // Organizations API
  async fetchOrganizations() {
    try {
      const response = await api.get('/api/admin/organizations');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching organizations:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch organizations',
        errorType: error.response?.data?.error || 'fetch_organizations_failed',
      };
    }
  }

  async fetchOrganizationsPaginated(params = {}) {
    try {
      const {
        page = 1,
        limit = 50,
        search = null,
        sort_by = 'create_date',
        sort_order = 'desc',
        company_id = null,
        min_session_count = null,
      } = params;

      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());

      if (search && search.trim()) {
        queryParams.append('search', search.trim());
      }

      queryParams.append('sort_by', sort_by);
      queryParams.append('sort_order', sort_order);

      if (company_id) {
        queryParams.append('company_id', company_id);
      }

      if (min_session_count !== null && min_session_count !== undefined) {
        queryParams.append('min_session_count', min_session_count.toString());
      }

      const response = await api.get(
        `/api/admin/organizations/paginated?${queryParams.toString()}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching paginated organizations:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch organizations',
        errorType:
          error.response?.data?.error || 'fetch_organizations_paginated_failed',
      };
    }
  }

  async fetchOrganizationById(organizationId) {
    try {
      const response = await api.get(
        `/api/admin/organizations/${organizationId}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching organization by ID:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch organization',
        errorType: error.response?.data?.error || 'fetch_organization_failed',
      };
    }
  }

  async deleteOrganization(organizationId) {
    try {
      const response = await api.delete(
        `/api/admin/organizations/${organizationId}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting organization:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete organization',
        errorType: error.response?.data?.error || 'delete_organization_failed',
      };
    }
  }

  async fetchOrganizationUsers(organizationId) {
    try {
      const response = await api.get(
        `/api/admin/organizations/${organizationId}/users`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching organization users:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to fetch organization users',
        errorType:
          error.response?.data?.error || 'fetch_organization_users_failed',
      };
    }
  }

  // Subscription Plans API
  async fetchSubscriptionPlans() {
    try {
      const response = await api.get('/api/admin/subscription-plans');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to fetch subscription plans',
        errorType:
          error.response?.data?.error || 'fetch_subscription_plans_failed',
      };
    }
  }

  // Update Company Subscription API
  async updateCompanySubscription(companyId, subscriptionData) {
    try {
      const response = await api.put(
        `/api/admin/companies/${companyId}/subscription`,
        subscriptionData,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating company subscription:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to update company subscription',
        errorType:
          error.response?.data?.error || 'update_company_subscription_failed',
      };
    }
  }

  async updateCompanyIsTest(companyId, isTest) {
    try {
      const response = await api.put(
        `/api/admin/companies/${companyId}/is-test`,
        {
          is_test: isTest,
        },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating company is_test flag:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to update company is_test flag',
        errorType:
          error.response?.data?.error || 'update_company_is_test_failed',
      };
    }
  }

  // Company Feature Flags API
  async fetchCompanyFeatureFlags(companyId) {
    try {
      const response = await api.get(
        `/api/admin/companies/${companyId}/feature-flags`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching company feature flags:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch company feature flags',
        errorType:
          error.response?.data?.error || 'fetch_company_feature_flags_failed',
      };
    }
  }

  async updateCompanyFeatureFlag(flagId, featureValue) {
    try {
      const response = await api.put(
        `/api/admin/company-feature-flags/${flagId}`,
        {
          feature_value: featureValue,
        },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating company feature flag:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to update company feature flag',
        errorType:
          error.response?.data?.error || 'update_company_feature_flag_failed',
      };
    }
  }

  async createCompanyFeatureFlag(companyId, featureFlagData) {
    try {
      const response = await api.post(
        `/api/admin/companies/${companyId}/feature-flags`,
        featureFlagData,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating company feature flag:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to create company feature flag',
        errorType:
          error.response?.data?.error || 'create_company_feature_flag_failed',
      };
    }
  }

  async deleteCompanyFeatureFlag(flagId) {
    try {
      const response = await api.delete(
        `/api/admin/company-feature-flags/${flagId}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting company feature flag:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to delete company feature flag',
        errorType:
          error.response?.data?.error || 'delete_company_feature_flag_failed',
      };
    }
  }

  // User Feature Flags API
  async fetchUserFeatureFlags(userId) {
    try {
      const response = await api.get(
        `/api/admin/users/${userId}/feature-flags`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching user feature flags:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to fetch user feature flags',
        errorType:
          error.response?.data?.error || 'fetch_user_feature_flags_failed',
      };
    }
  }

  async updateUserFeatureFlag(flagId, featureValue) {
    try {
      const response = await api.put(
        `/api/admin/user-feature-flags/${flagId}`,
        {
          feature_value: featureValue,
        },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating user feature flag:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to update user feature flag',
        errorType:
          error.response?.data?.error || 'update_user_feature_flag_failed',
      };
    }
  }

  async createUserFeatureFlag(userId, featureFlagData) {
    try {
      const response = await api.post(
        `/api/admin/users/${userId}/feature-flags`,
        featureFlagData,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating user feature flag:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to create user feature flag',
        errorType:
          error.response?.data?.error || 'create_user_feature_flag_failed',
      };
    }
  }

  async deleteUserFeatureFlag(flagId) {
    try {
      const response = await api.delete(
        `/api/admin/user-feature-flags/${flagId}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting user feature flag:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to delete user feature flag',
        errorType:
          error.response?.data?.error || 'delete_user_feature_flag_failed',
      };
    }
  }

  // Plan Quota Policies API
  //
  // A plan's quota policies are its entitlement. An empty list means every feature is
  // unmetered on that plan — that is how "unlimited" is expressed, not zero.
  async fetchPlanQuotas(planId) {
    try {
      const response = await api.get(
        `/api/admin/subscription-plans/${planId}/quotas`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching plan quotas:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch plan quotas',
        errorType: error.response?.data?.error || 'fetch_plan_quotas_failed',
      };
    }
  }

  // Sets allowances on a plan. This affects EVERY company on that plan.
  // `quotas`: [{ feature, feature_available_amount }]
  // `remove`: [feature] — deletes the policy, making the feature unmetered.
  async updatePlanQuotas(planId, { quotas = [], remove = [] } = {}) {
    try {
      const response = await api.put(
        `/api/admin/subscription-plans/${planId}/quotas`,
        { quotas, remove },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating plan quotas:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update plan quotas',
        errorType: error.response?.data?.error || 'update_plan_quotas_failed',
      };
    }
  }

  // Company Quota Usages API
  async fetchCompanyQuotaUsages(companyId) {
    try {
      const response = await api.get(
        `/api/admin/companies/${companyId}/quota-usages`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching company quota usages:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch company quota usages',
        errorType:
          error.response?.data?.error || 'fetch_company_quota_usages_failed',
      };
    }
  }

  // Reset Quota Usage API
  async resetQuotaUsage(quotaId) {
    try {
      const response = await api.post(
        `/api/admin/quota-usage/${quotaId}/reset`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error resetting quota usage:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to reset quota usage',
        errorType: error.response?.data?.error || 'reset_quota_usage_failed',
      };
    }
  }

  // Company Discount Code Usages API
  async fetchCompanyDiscountCodeUsages(companyId) {
    try {
      const response = await api.get(
        `/api/admin/companies/${companyId}/discount-code-usages`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching company discount code usages:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch company discount code usages',
        errorType:
          error.response?.data?.error ||
          'fetch_company_discount_code_usages_failed',
      };
    }
  }

  // Delete Discount Code Usage API
  async deleteDiscountCodeUsage(usageId) {
    try {
      const response = await api.delete(
        `/api/admin/discount-code-usage/${usageId}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting discount code usage:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to delete discount code usage',
        errorType:
          error.response?.data?.error || 'delete_discount_code_usage_failed',
      };
    }
  }

  // Feature Codes API
  async fetchAllFeatureCodes() {
    try {
      const response = await api.get('/api/admin/feature-codes');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching all feature codes:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch feature codes',
        errorType: error.response?.data?.error || 'fetch_feature_codes_failed',
      };
    }
  }

  // Discount Codes API
  async fetchAllDiscountCodes() {
    try {
      const response = await api.get('/api/admin/discount-codes');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching all discount codes:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to fetch discount codes',
        errorType: error.response?.data?.error || 'fetch_discount_codes_failed',
      };
    }
  }

  // Company Feature Code Usages API
  async fetchCompanyFeatureCodeUsages(companyId) {
    try {
      const response = await api.get(
        `/api/admin/companies/${companyId}/feature-code-usages`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching company feature code usages:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch company feature code usages',
        errorType:
          error.response?.data?.error ||
          'fetch_company_feature_code_usages_failed',
      };
    }
  }

  // Delete Feature Code Usage API
  async deleteFeatureCodeUsage(usageId) {
    try {
      const response = await api.delete(
        `/api/admin/feature-code-usage/${usageId}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting feature code usage:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to delete feature code usage',
        errorType:
          error.response?.data?.error || 'delete_feature_code_usage_failed',
      };
    }
  }

  // Update Feature Code API
  async updateFeatureCode(codeId, updateData) {
    try {
      const response = await api.put(
        `/api/admin/feature-codes/${codeId}`,
        updateData,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating feature code:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update feature code',
        errorType: error.response?.data?.error || 'update_feature_code_failed',
      };
    }
  }

  // Update Discount Code API
  async updateDiscountCode(codeId, updateData) {
    try {
      const response = await api.put(
        `/api/admin/discount-codes/${codeId}`,
        updateData,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating discount code:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to update discount code',
        errorType: error.response?.data?.error || 'update_discount_code_failed',
      };
    }
  }

  // Create Feature Code API
  async createFeatureCode(createData) {
    try {
      const response = await api.post('/api/admin/feature-codes', createData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating feature code:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create feature code',
        errorType: error.response?.data?.error || 'create_feature_code_failed',
      };
    }
  }

  // Create Discount Code API
  async createDiscountCode(createData) {
    try {
      const response = await api.post('/api/admin/discount-codes', createData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating discount code:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to create discount code',
        errorType: error.response?.data?.error || 'create_discount_code_failed',
      };
    }
  }

  // Delete Feature Code API
  async deleteFeatureCode(codeId) {
    try {
      const response = await api.delete(`/api/admin/feature-codes/${codeId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting feature code:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete feature code',
        errorType: error.response?.data?.error || 'delete_feature_code_failed',
      };
    }
  }

  // Delete Discount Code API
  async deleteDiscountCode(codeId) {
    try {
      const response = await api.delete(`/api/admin/discount-codes/${codeId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting discount code:', error);
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to delete discount code',
        errorType: error.response?.data?.error || 'delete_discount_code_failed',
      };
    }
  }

  // Apply Coupon API
  async applyCouponForCompany(companyId, couponCode) {
    try {
      const response = await api.post(
        `/api/admin/companies/${companyId}/apply-coupon`,
        {
          coupon_code: couponCode,
        },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error applying coupon:', error);

      // Handle specific error cases based on backend responses
      const statusCode = error.response?.status;
      const errorMessage =
        error.response?.data?.detail || error.response?.data?.message;

      let userFriendlyMessage = 'Failed to apply coupon';
      let errorType = 'apply_coupon_failed';

      if (statusCode === 404) {
        if (errorMessage?.includes('Company with ID')) {
          userFriendlyMessage = 'Company not found';
          errorType = 'company_not_found';
        } else if (errorMessage?.includes('Coupon not found')) {
          userFriendlyMessage =
            'Coupon code not found. Please check the code and try again.';
          errorType = 'coupon_not_found';
        } else {
          userFriendlyMessage = 'Coupon not found';
          errorType = 'coupon_not_found';
        }
      } else if (statusCode === 400) {
        if (errorMessage?.includes('Coupon expired')) {
          userFriendlyMessage = 'This coupon has expired and cannot be used.';
          errorType = 'coupon_expired';
        } else {
          userFriendlyMessage = errorMessage || 'Invalid coupon code';
          errorType = 'invalid_coupon';
        }
      } else if (statusCode === 409) {
        if (errorMessage?.includes('already has this discount code')) {
          userFriendlyMessage =
            'This discount code is already applied to this company.';
          errorType = 'discount_code_already_applied';
        } else if (errorMessage?.includes('already has this feature code')) {
          userFriendlyMessage =
            'This feature code is already applied to this company.';
          errorType = 'feature_code_already_applied';
        } else {
          userFriendlyMessage =
            'This coupon is already applied to this company.';
          errorType = 'coupon_already_applied';
        }
      } else if (statusCode === 500) {
        userFriendlyMessage =
          'Server error occurred while applying coupon. Please try again later.';
        errorType = 'server_error';
      } else {
        userFriendlyMessage =
          errorMessage || 'Failed to apply coupon. Please try again.';
      }

      return {
        success: false,
        error: userFriendlyMessage,
        errorType: errorType,
      };
    }
  }

  // Helper method to format date
  formatDate(dateString) {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  }

  // Helper method to get initials from name
  getInitials(name) {
    if (!name) return '?';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  // Rule Engine Definitions API
  async fetchRuleEngineDefinitions(queryParams = {}) {
    try {
      const params = new URLSearchParams();

      // Add query parameters if provided and not empty
      if (queryParams.project_id && queryParams.project_id.trim() !== '') {
        params.append('project_id', queryParams.project_id);
      }
      if (queryParams.stage && queryParams.stage.trim() !== '') {
        params.append('stage', queryParams.stage);
      }
      if (
        queryParams.active !== null &&
        queryParams.active !== undefined &&
        queryParams.active !== ''
      ) {
        params.append('active', queryParams.active.toString());
      }

      const url = `/api/admin/rule-engine-definitions${
        params.toString() ? `?${params.toString()}` : ''
      }`;
      const response = await api.get(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching rule engine definitions:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch rule engine definitions',
        errorType:
          error.response?.data?.error || 'fetch_rule_engine_definitions_failed',
      };
    }
  }

  async fetchOrganizationRuleEngineDefinitions(
    organizationId,
    queryParams = {},
  ) {
    try {
      const params = new URLSearchParams();

      // Add query parameters if provided and not empty
      if (queryParams.stage && queryParams.stage.trim() !== '') {
        params.append('stage', queryParams.stage);
      }
      if (
        queryParams.active !== null &&
        queryParams.active !== undefined &&
        queryParams.active !== ''
      ) {
        params.append('active', queryParams.active.toString());
      }

      const url = `/api/admin/organizations/${organizationId}/rule-engine-definitions${
        params.toString() ? `?${params.toString()}` : ''
      }`;
      const response = await api.get(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error(
        'Error fetching organization rule engine definitions:',
        error,
      );
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch organization rule engine definitions',
        errorType:
          error.response?.data?.error ||
          'fetch_organization_rule_engine_definitions_failed',
      };
    }
  }

  async fetchRuleEngineDefinitionById(ruleId) {
    try {
      const response = await api.get(
        `/api/admin/rule-engine-definitions/${ruleId}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching rule engine definition:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to fetch rule engine definition',
        errorType:
          error.response?.data?.error || 'fetch_rule_engine_definition_failed',
      };
    }
  }

  async createRuleEngineDefinition(createData) {
    try {
      const response = await api.post(
        '/api/admin/rule-engine-definitions',
        createData,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating rule engine definition:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to create rule engine definition',
        errorType:
          error.response?.data?.error || 'create_rule_engine_definition_failed',
      };
    }
  }

  async updateRuleEngineDefinition(ruleId, updateData) {
    try {
      const response = await api.put(
        `/api/admin/rule-engine-definitions/${ruleId}`,
        updateData,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating rule engine definition:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to update rule engine definition',
        errorType:
          error.response?.data?.error || 'update_rule_engine_definition_failed',
      };
    }
  }

  async deleteRuleEngineDefinition(ruleId) {
    try {
      const response = await api.delete(
        `/api/admin/rule-engine-definitions/${ruleId}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting rule engine definition:', error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Failed to delete rule engine definition',
        errorType:
          error.response?.data?.error || 'delete_rule_engine_definition_failed',
      };
    }
  }

  // Fetch Projects (Organizations) for dropdown
  async fetchProjects() {
    try {
      const response = await api.get('/api/admin/organizations');
      return {
        success: true,
        data: response.data?.organizations || [],
      };
    } catch (error) {
      console.error('Error fetching projects:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch projects',
        errorType: error.response?.data?.error || 'fetch_projects_failed',
      };
    }
  }

  // Ghost User Management API
  async fetchGhostUsers(companyId) {
    try {
      const response = await api.get(
        `/api/admin/companies/${companyId}/ghost-users`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching ghost users:', error);
      return {
        success: false,
        error:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to fetch ghost users',
        errorType: error.response?.data?.error || 'fetch_ghost_users_failed',
      };
    }
  }

  async checkGhostStatus(companyId) {
    try {
      const response = await api.get(
        `/api/admin/companies/${companyId}/ghost-status`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error checking ghost status:', error);
      return {
        success: false,
        error:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to check ghost status',
        errorType: error.response?.data?.error || 'check_ghost_status_failed',
      };
    }
  }

  async addGhostToCompany(companyId) {
    try {
      const response = await api.post(
        `/api/admin/companies/${companyId}/add-ghost`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error adding ghost to company:', error);
      return {
        success: false,
        error:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to add ghost to company',
        errorType: error.response?.data?.error || 'add_ghost_failed',
      };
    }
  }

  async removeGhostFromCompany(companyId) {
    try {
      const response = await api.delete(
        `/api/admin/companies/${companyId}/remove-ghost`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error removing ghost from company:', error);
      return {
        success: false,
        error:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to remove ghost from company',
        errorType: error.response?.data?.error || 'remove_ghost_failed',
      };
    }
  }

  // Conversation Statistics API
  async fetchCompanyConversationStats(companyId) {
    try {
      const response = await api.get(
        `/api/admin/companies/${companyId}/conversation-statistics`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching conversation statistics:', error);
      return {
        success: false,
        error:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to fetch conversation statistics',
        errorType:
          error.response?.data?.error || 'fetch_conversation_stats_failed',
      };
    }
  }

  // Organization Session Statistics API - Updated to use query parameters
  async fetchOrganizationSessionStats(organizationId = null, period = 'MONTH') {
    try {
      const params = new URLSearchParams();
      params.append('period', period);

      if (organizationId) {
        params.append('organization_id', organizationId);
      }

      const response = await api.get(
        `/api/admin/organizations/session-statistics?${params.toString()}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching organization session statistics:', error);
      return {
        success: false,
        error:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to fetch session statistics',
        errorType: error.response?.data?.error || 'fetch_session_stats_failed',
      };
    }
  }

  // Organization Funds API
  async fetchOrganizationFunds(organizationId) {
    try {
      const response = await api.get(
        `/api/admin/organizations/${organizationId}/funds`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching organization funds:', error);
      return {
        success: false,
        error:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to fetch organization funds',
        errorType:
          error.response?.data?.error || 'fetch_organization_funds_failed',
      };
    }
  }

  async updateOrganizationFunds(organizationId, funds) {
    try {
      const response = await api.put(
        `/api/admin/organizations/${organizationId}/funds`,
        {
          funds: funds,
        },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating organization funds:', error);
      return {
        success: false,
        error:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to update organization funds',
        errorType:
          error.response?.data?.error || 'update_organization_funds_failed',
      };
    }
  }
}

const apiService = new ApiService();
export default apiService;
